import { DataProvider, HttpError } from '@refinedev/core';
import { acquireSuperAdminToken } from '../auth/msalConfig';
import { getScopeCompanyId } from '../scope/companyScope';
import { apiBaseUrl } from './env';

/**
 * Refine-shaped data provider against the superadmin Function App endpoints.
 *
 * Base URL: VITE_SUPERADMIN_API_BASE (or empty string → Vite dev proxy).
 * Auth: Bearer token from acquireSuperAdminToken() (workforce tenant).
 * Routes: /v1/superadmin/<resource> collections, /v1/superadmin/<resource>/:id items.
 *
 * Tenant-scoped calls (leads, users, conferences by tenant) append
 * ?companyId=<chosen> — the CompanyScopePicker sets the active companyId
 * and callers must inject it. The backend re-validates it server-side.
 */

const API_BASE_URL: string = apiBaseUrl; // empty → uses Vite dev proxy

interface BackendError {
  error: {
    code: string;
    message: string;
    details?: { fieldErrors?: Record<string, string[]> };
  };
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await acquireSuperAdminToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function fetchJson<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = await authHeaders();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers as Record<string, string>) },
  });
  if (!response.ok) {
    let body: BackendError | null = null;
    try {
      body = (await response.json()) as BackendError;
    } catch {
      // non-JSON error body — fall through with synthetic message
    }
    const httpError: HttpError & { retryAfter?: number } = {
      statusCode: response.status,
      message: body?.error?.message ?? response.statusText,
      errors: body?.error?.details?.fieldErrors,
    };
    // A 429 carries `Retry-After` (seconds until the caller's next write
    // token — the backend's rateLimit middleware, which the inline lead-fields
    // route consumes). Kept on the error, as the portal's fetchJson does, so
    // the Batch grid's sync store can wait the window out; every other caller
    // ignores it. Optional-chained: a stubbed Response may carry no headers.
    const retryAfter = Number(response.headers?.get?.('Retry-After'));
    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      httpError.retryAfter = retryAfter;
    }
    throw httpError;
  }
  if (response.status === 204) {
    return undefined as unknown as T;
  }
  return (await response.json()) as T;
}

/**
 * A handful of resources don't fit the flat `/v1/superadmin/{resource}` shape.
 * The CRM connections endpoint is nested (`/v1/superadmin/crm/connections`), so
 * the `crm-connections` Refine resource maps to the `crm/connections` path
 * segment here. Everything else falls through unchanged.
 */
const RESOURCE_PATH_OVERRIDES: Record<string, string> = {
  'crm-connections': 'crm/connections',
};

function resourcePath(resource: string, id?: string | number): string {
  const seg = RESOURCE_PATH_OVERRIDES[resource] ?? resource;
  return id !== undefined
    ? `/v1/superadmin/${seg}/${id}`
    : `/v1/superadmin/${seg}`;
}

/**
 * Resources that must NEVER inherit the global "operate as tenant" scope:
 *  - `companies`    — the tenant registry itself (listing tenants, not tenant data).
 *  - `toolkits`     — global catalog, not tenant-scoped.
 *  - `integrations` / `crm-connections` — CRM/Integrations surface. The Salesforce
 *    dialog manages every company's connections and passes its OWN explicit
 *    companyId; the global scope must never narrow (or fight) that.
 * Everything else (`users`, `conferences`) is scope-aware.
 */
const SCOPE_EXEMPT_RESOURCES = new Set([
  'companies',
  'toolkits',
  'integrations',
  'crm-connections',
  // AI prompt store is global (not tenant-scoped).
  'prompt-templates',
]);

/**
 * The tenant scope to append for `resource`, or `null` when none applies:
 * skipped for exempt resources, and skipped when the caller already passed an
 * explicit `meta.companyId` (explicit always wins over the global scope).
 */
function scopeCompanyIdFor(
  resource: string,
  explicitCompanyId: string | undefined,
): string | null {
  if (explicitCompanyId) return null;
  if (SCOPE_EXEMPT_RESOURCES.has(resource)) return null;
  return getScopeCompanyId();
}

const RESOURCE_COLLECTION_KEY: Record<string, string> = {
  companies: 'companies',
  conferences: 'conferences',
  users: 'users',
  toolkits: 'toolkits',
  integrations: 'integrations',
  questions: 'questions',
  // CRM list body is `{ connections: [...] }` (backend crmConnections handler).
  'crm-connections': 'connections',
  // Prompt-templates list body is `{ promptTemplates: [...] }`.
  'prompt-templates': 'promptTemplates',
};

function unwrapList<T>(
  resource: string,
  body: Record<string, unknown>,
): { data: T[]; total: number } {
  const key = RESOURCE_COLLECTION_KEY[resource] ?? resource;
  const data = ((body[key] as T[]) ?? []) as T[];
  return { data, total: data.length };
}

export const dataProvider: DataProvider = {
  getApiUrl: () => API_BASE_URL,

  getList: async ({ resource, meta }) => {
    const m = meta as
      | { companyId?: string | null; archived?: boolean }
      | undefined;
    const params = new URLSearchParams();
    // Order matters for the tests + readable URLs: archived precedes companyId.
    if (m?.archived === true) params.set('archived', 'true');
    // A caller that passes the `companyId` key at all — including explicit
    // `null` — always wins over the global tenant scope, even for a
    // scope-aware resource. This is how a caller forces the cross-tenant
    // union on a resource that's normally scope-aware: e.g. the toolkit
    // "apply to conferences" picker (AppliedConferencesModal) must show every
    // tenant's conferences regardless of the active "operate as" scope, so it
    // passes `meta: { companyId: null }` to explicitly opt out — distinct
    // from omitting `meta.companyId` entirely, which still inherits the
    // global scope below.
    const companyId =
      m && 'companyId' in m
        ? m.companyId
        : scopeCompanyIdFor(resource, undefined);
    if (companyId) params.set('companyId', companyId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const body = await fetchJson<Record<string, unknown>>(
      resourcePath(resource) + qs,
    );
    return unwrapList(resource, body);
  },

  getOne: async ({ resource, id, meta }) => {
    const explicit = (meta as { companyId?: string } | undefined)?.companyId;
    const companyId = explicit ?? scopeCompanyIdFor(resource, explicit);
    const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    const data = await fetchJson<Record<string, unknown>>(
      resourcePath(resource, id as string) + qs,
    );
    return { data: data as never };
  },

  create: async ({ resource, variables, meta }) => {
    // Explicit meta.companyId → append ?companyId= (e.g. the per-tenant
    // Questions Inventory create). Resources that don't pass it are unchanged.
    const explicit = (meta as { companyId?: string } | undefined)?.companyId;
    const qs = explicit ? `?companyId=${encodeURIComponent(explicit)}` : '';
    const data = await fetchJson<Record<string, unknown>>(
      resourcePath(resource) + qs,
      { method: 'POST', body: JSON.stringify(variables) },
    );
    return { data: data as never };
  },

  update: async ({ resource, id, variables, meta }) => {
    const explicit = (meta as { companyId?: string } | undefined)?.companyId;
    const qs = explicit ? `?companyId=${encodeURIComponent(explicit)}` : '';
    const data = await fetchJson<Record<string, unknown>>(
      resourcePath(resource, id as string) + qs,
      { method: 'PATCH', body: JSON.stringify(variables) },
    );
    return { data: data as never };
  },

  deleteOne: async ({ resource, id, meta }) => {
    const explicit = (meta as { companyId?: string } | undefined)?.companyId;
    const qs = explicit ? `?companyId=${encodeURIComponent(explicit)}` : '';
    await fetchJson<void>(resourcePath(resource, id as string) + qs, {
      method: 'DELETE',
    });
    return { data: { id } as never };
  },

  getMany: async () => {
    throw new Error('getMany not implemented; use getOne per id');
  },
  createMany: async () => {
    throw new Error('createMany not implemented');
  },
  deleteMany: async () => {
    throw new Error('deleteMany not implemented');
  },
  updateMany: async () => {
    throw new Error('updateMany not implemented');
  },
  custom: async ({ url, method, payload, headers }) => {
    const data = await fetchJson<Record<string, unknown>>(url, {
      method: method ?? 'GET',
      ...(payload ? { body: JSON.stringify(payload) } : {}),
      ...(headers
        ? { headers: headers as unknown as Record<string, string> }
        : {}),
    });
    return { data: data as never };
  },
};

/**
 * Direct fetch helper for endpoints that don't fit the Refine resource shape.
 * Same auth + error translation as dataProvider.
 */
export async function superadminFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  return fetchJson<T>(path, init);
}

/**
 * Variant of superadminFetch that returns the raw fetch Response instead of a
 * parsed JSON body. Used by the CSV export flow where the caller needs the
 * response stream (`blob()`) + the filename from `Content-Disposition`, not a
 * parsed body. Same auth + error envelope as superadminFetch — on non-OK
 * responses the JSON error is parsed and thrown as HttpError. (Mirrors the
 * customer portal's `adminFetchRaw`.)
 */
export async function superadminFetchRaw(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = await authHeaders();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers as Record<string, string>) },
  });
  if (!response.ok) {
    const text = await response.text();
    let message = `Request failed with status ${response.status}`;
    try {
      const body = JSON.parse(text) as BackendError;
      message = body.error?.message ?? message;
    } catch {
      /* not JSON — keep the default */
    }
    throw { statusCode: response.status, message } as HttpError;
  }
  return response;
}
